import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../store/hooks';
import { usePromotionFlow } from '../../hooks/usePromotionFlow';
import { setFlowToken, setPhone, setSentOffer } from '../../store/mainSlice';
import FlowLayout from '../FlowLayout';
import { useA11yTokens } from '../../hooks/useA11yTokens';
import { DeliveryMethodTypes, PromotionPackageEstateEntity } from '../../types/promotion';
import { useDirection } from '../../hooks/useDirection';
import { useIsDesktopDevice } from '../../hooks/useIsDesktopDevice';

const periodLabel = (estate: PromotionPackageEstateEntity, t: (key: string, opts?: Record<string, unknown>) => string) => {
    const months = estate.endDateByMonths - estate.startDateByMonths;
    return t('offer.monthsDiscount', { count: months, percent: estate.percentageDiscount });
};

const Offer: React.FC = () => {
    const { t } = useTranslation();
    const theme4dTokens = useA11yTokens();
    const isDesktop = useIsDesktopDevice();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { createOffer } = usePromotionFlow();
    const { isLTR } = useDirection();
    const { flowToken, selectedContactToken, promotionPackage, isLoading, packageToken, env } = useAppSelector((state) => state.main);

    React.useEffect(() => {
        if (!flowToken || !selectedContactToken || !promotionPackage) {
            dispatch(setPhone(''));
            dispatch(setFlowToken(''));
            navigate(packageToken ? `/?token=${encodeURIComponent(packageToken)}&ENV=${env}` : '/');
        }
    }, []);

    const onSendOffer = async () => {
        const result = await createOffer(flowToken, selectedContactToken);
        if (result) {
            dispatch(setSentOffer({ method: Number(result.deliveryMethodId) as DeliveryMethodTypes, addressingId: result.addressingId }));
            navigate('/offer-success');
        } else {
            navigate('/error');
        }
    };

    if (!promotionPackage) return null;

    const discountedEstates = promotionPackage.estates.filter(
        (estate) => estate.percentageDiscount > 0 && !(estate.startDateByMonths === 0 && estate.endDateByMonths === 0)
    );

    return (
        <FlowLayout>
            <Typography sx={{ fontFamily: theme4dTokens.headFont, fontSize: isDesktop ? 27 : 25, fontWeight: 700, lineHeight: 1.25, color: theme4dTokens.text, m: 0, wordBreak: 'break-word', ...(isDesktop && { mt: '50px' }) }}>
                {promotionPackage.name}
            </Typography>

            {discountedEstates.length > 0 && (
                <Box sx={{ border: `1px solid ${theme4dTokens.border}`, borderRadius: theme4dTokens.radius, overflow: 'hidden', bgcolor: theme4dTokens.surface, ...(isDesktop && { mt: '20px' }) }}>
                    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: isDesktop ? 15 : 13 }}>
                        <Box component="thead">
                            <Box component="tr">
                                <Box
                                    component="th"
                                    sx={{ width: '50%', textAlign: isLTR ? 'left' : 'right', p: '9px 12px', fontSize: isDesktop ? 13 : 11, color: theme4dTokens.muted, fontWeight: 700, borderBottom: `1px solid ${theme4dTokens.border}`, whiteSpace: 'normal', wordBreak: 'break-word' }}
                                >
                                    {t('offer.product')}
                                </Box>
                                <Box
                                    component="th"
                                    sx={{ width: '50%', textAlign: 'left', p: '9px 12px', fontSize: isDesktop ? 13 : 11, color: theme4dTokens.muted, fontWeight: 700, borderBottom: `1px solid ${theme4dTokens.border}`, whiteSpace: 'normal', wordBreak: 'break-word' }}
                                >
                                    {t('offer.period')}
                                </Box>
                            </Box>
                        </Box>
                        <Box component="tbody">
                            {discountedEstates.map((estate, i) => {
                                const isLast = i === discountedEstates.length - 1;
                                return (
                                    <Box component="tr" key={estate.rowGuid}>
                                        <Box
                                            component="td"
                                            sx={{ width: '50%', textAlign: isLTR ? 'left' : 'right', p: '10px 12px', verticalAlign: 'top', borderBottom: isLast ? undefined : `1px solid ${theme4dTokens.border}` }}
                                        >
                                            <Box component="span" sx={{ display: 'block', fontSize: isDesktop ? 16 : 14, fontWeight: 700, color: theme4dTokens.text, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                {estate.estateTypeName}
                                            </Box>
                                        </Box>
                                        <Box
                                            component="td"
                                            sx={{
                                                width: '50%',
                                                textAlign: 'left',
                                                p: '10px 12px',
                                                whiteSpace: 'normal',
                                                wordBreak: 'break-word',
                                                color: theme4dTokens.text,
                                                borderBottom: isLast ? undefined : `1px solid ${theme4dTokens.border}`,
                                            }}
                                        >
                                            {periodLabel(estate, t)}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                </Box>
            )}

            <Typography sx={{ fontSize: isDesktop ? 14.5 : 12.5, fontWeight: 700, color: theme4dTokens.text, textAlign: 'center', lineHeight: 1.5, m: 0, wordBreak: 'break-word', ...(isDesktop && { mt: '16px' }) }}>
                {t('offer.premiumBonus')}
            </Typography>

            <Button
                variant="contained"
                fullWidth
                disableElevation
                disabled={isLoading}
                onClick={onSendOffer}
                sx={{
                    fontFamily: theme4dTokens.headFont,
                    bgcolor: theme4dTokens.accent,
                    color: theme4dTokens.accentText,
                    borderRadius: theme4dTokens.radius,
                    py: '15px',
                    fontSize: isDesktop ? 18 : 16,
                    fontWeight: 700,
                    mt: 'auto',
                    '&:hover': { bgcolor: theme4dTokens.accent, filter: 'brightness(1.07)' },
                }}
            >
                {t('offer.send')}
            </Button>

            <Typography sx={{ fontSize: isDesktop ? 14.5 : 12.5, color: theme4dTokens.muted, textAlign: 'center', lineHeight: 1.5, m: 0, wordBreak: 'break-word' }}>
                {t('offer.note')}
            </Typography>
        </FlowLayout>
    );
};

export default Offer;
